/**
 * Title screen: floating gem logo, title, subtitle, menu buttons.
 * Recreates design's screen-title.jsx pixel-perfectly.
 */

import { GEM_TYPES } from "../render/theme";
import { htmlGem } from "../render/htmlSprites";
import { mountTutorialModal } from "./TutorialModal";
import { isMuted, startMusic, toggleMute } from "./Audio";

export function mountTitle(
  root: HTMLElement,
  onStart: () => void,
  onStartEndless: () => void,
  hasUnseenUpdate = false,
): () => void {
  const screen = document.createElement("div");
  screen.className = "title-screen";
  if (hasUnseenUpdate) screen.dataset.unseenUpdate = "true";

  // Logo row
  const logo = document.createElement("div");
  logo.className = "title-logo";
  GEM_TYPES.forEach((g, i) => {
    const wrap = document.createElement("div");
    wrap.className = "title-logo-gem";
    wrap.style.animationDelay = `${i * 0.18}s`;
    wrap.appendChild(htmlGem(g, 40, true));
    logo.appendChild(wrap);
  });
  screen.appendChild(logo);

  // Title
  const titleH = document.createElement("div");
  titleH.className = "title-h1";
  titleH.innerHTML = `<div class="title-name px-h">FACETS</div>`;
  screen.appendChild(titleH);

  // Subtitle
  const sub = document.createElement("div");
  sub.className = "title-subtitle";
  sub.innerHTML = `A GEM TOWER DEFENSE.`;
  screen.appendChild(sub);

  // Menu
  const menu = document.createElement("div");
  menu.className = "title-menu";
  const btns: Array<[string, string, () => void]> = [
    ["▶ NEW GAME", "px-btn px-btn-primary", onStart],
    ["∞ ENDLESS", "px-btn", onStartEndless],
    ["? HOW TO PLAY", "px-btn", () => mountTutorialModal(root)],
  ];
  for (const [label, cls, fn] of btns) {
    const b = document.createElement("button");
    b.className = cls;
    b.textContent = label;
    b.addEventListener("click", fn);
    menu.appendChild(b);
  }
  screen.appendChild(menu);

  // Update nudge toast
  if (hasUnseenUpdate) {
    const toast = document.createElement("div");
    toast.className = "title-update-toast title-toast-enter";
    const txt = document.createElement("span");
    txt.innerHTML = `v${__GAME_VERSION__} — <b>see what's new</b>`;
    const dismiss = document.createElement("button");
    dismiss.className = "title-update-dismiss";
    dismiss.textContent = "✕";
    const dismissToast = () => {
      toast.classList.add("title-toast-exit");
      toast.addEventListener("transitionend", () => toast.remove(), { once: true });
    };
    dismiss.addEventListener("click", (e) => {
      e.stopPropagation();
      dismissToast();
    });
    toast.append(txt, dismiss);
    toast.addEventListener("click", () => {
      toast.remove();
      mountTutorialModal(root, "CHANGES");
    });
    screen.appendChild(toast);
    void toast.offsetHeight;
    toast.classList.remove("title-toast-enter");
  }

  // Footer
  const footer = document.createElement("div");
  footer.className = "title-footer";

  const footerTop = document.createElement("div");
  footerTop.className = "title-footer-top";
  const version = document.createElement("span");
  version.textContent = `v${__GAME_VERSION__}`;
  footerTop.appendChild(version);

  const footerBottom = document.createElement("div");
  footerBottom.className = "title-footer-bottom";
  const muteBtn = document.createElement("button");
  muteBtn.className = "title-mute-btn";
  muteBtn.textContent = isMuted() ? "MUSIC OFF" : "MUSIC ON";
  muteBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const nowMuted = toggleMute();
    muteBtn.textContent = nowMuted ? "MUSIC OFF" : "MUSIC ON";
  });
  const sep = document.createElement("span");
  sep.textContent = "·";
  const credit = document.createElement("span");
  credit.className = "title-footer-credit";
  credit.textContent = "Music by hundredsense";
  footerBottom.append(muteBtn, sep, credit);

  footer.append(footerTop, footerBottom);
  screen.appendChild(footer);

  root.appendChild(screen);

  startMusic();

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      onStart();
    }
  };
  window.addEventListener("keydown", onKey);

  return () => {
    window.removeEventListener("keydown", onKey);
    screen.remove();
  };
}
